import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ClipboardList, 
  CheckCircle, 
  Circle, 
  Clock,
  Filter,
  Plus,
  Pencil,
  Trash2,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ActionLog = () => {
  const { isELT, isAdmin } = useAuth();
  const { selectedYear, periodType } = useApp();
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [functionFilter, setFunctionFilter] = useState('all');
  
  // Dialog states
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    description: '',
    owner: '',
    function: 'brokerage',
    due_date: '',
    status: 'open',
    period_type: 'monthly',
    year: 2025
  });

  const fetchActions = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (functionFilter !== 'all') params.function = functionFilter;
      
      const response = await axios.get(`${API}/action-items`, { params });
      setActions(response.data);
    } catch (error) {
      console.error('Failed to fetch actions:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, functionFilter]);

  useEffect(() => {
    fetchActions();
  }, [fetchActions]);

  const resetForm = () => {
    setFormData({
      description: '',
      owner: '',
      function: 'brokerage',
      due_date: '',
      status: 'open',
      period_type: periodType,
      year: selectedYear
    });
  };

  const handleAddAction = async () => {
    if (!formData.description || !formData.owner || !formData.due_date) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      await axios.post(`${API}/action-items`, formData);
      toast.success('Action item created');
      setIsAddDialogOpen(false);
      resetForm();
      fetchActions();
    } catch (error) {
      toast.error('Failed to create action item');
    }
  };

  const handleEditAction = async () => {
    if (!formData.description || !formData.owner || !formData.due_date) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      await axios.put(`${API}/action-items/${selectedAction.id}`, {
        description: formData.description,
        owner: formData.owner,
        due_date: formData.due_date,
        status: formData.status,
        function: formData.function
      });
      toast.success('Action item updated');
      setIsEditDialogOpen(false);
      setSelectedAction(null);
      fetchActions();
    } catch (error) {
      toast.error('Failed to update action item');
    }
  };

  const handleDeleteAction = async () => {
    try {
      await axios.delete(`${API}/action-items/${selectedAction.id}`);
      toast.success('Action item deleted');
      setIsDeleteDialogOpen(false);
      setSelectedAction(null);
      fetchActions();
    } catch (error) {
      toast.error('Failed to delete action item');
    }
  };

  const handleStatusChange = async (actionId, newStatus) => {
    try {
      await axios.put(`${API}/action-items/${actionId}`, { status: newStatus });
      toast.success('Status updated');
      fetchActions();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const openEditDialog = (action) => {
    setSelectedAction(action);
    setFormData({
      description: action.description,
      owner: action.owner,
      function: action.function,
      due_date: action.due_date,
      status: action.status,
      period_type: action.period_type,
      year: action.year
    });
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (action) => {
    setSelectedAction(action);
    setIsDeleteDialogOpen(true);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'complete':
        return <CheckCircle className="w-4 h-4 text-emerald-600" />;
      case 'in_progress':
        return <Clock className="w-4 h-4 text-amber-600" />;
      default:
        return <Circle className="w-4 h-4 text-slate-400" />;
    }
  };

  const stats = {
    total: actions.length,
    open: actions.filter(a => a.status === 'open').length,
    inProgress: actions.filter(a => a.status === 'in_progress').length,
    complete: actions.filter(a => a.status === 'complete').length,
    overdue: actions.filter(a => new Date(a.due_date) < new Date() && a.status !== 'complete').length
  };

  const functions = [
    { value: 'all', label: 'All Functions' },
    { value: 'brokerage', label: 'Brokerage' },
    { value: 'mga', label: 'MGA' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'finance', label: 'Finance' },
    { value: 'people', label: 'People' },
  ];

  const statuses = [
    { value: 'all', label: 'All Statuses' },
    { value: 'open', label: 'Open' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'complete', label: 'Complete' },
  ];

  const canEdit = isELT() || isAdmin();

  return (
    <Layout>
      <Header title="Action Log" subtitle="Track and manage action items across all functions" />
      
      <div className="p-4 sm:p-6 space-y-6" data-testid="action-log">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-5">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-500">Total</span>
              <ClipboardList className="w-5 h-5 text-slate-400" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-slate-900 mt-2">{stats.total}</p>
          </div>
          
          <div className="bg-blue-50 rounded-lg border border-blue-200 p-4 sm:p-5">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-blue-700">Open</span>
              <Circle className="w-5 h-5 text-blue-600" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-blue-800 mt-2">{stats.open}</p>
          </div>
          
          <div className="bg-amber-50 rounded-lg border border-amber-200 p-4 sm:p-5">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-amber-700">In Progress</span>
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-amber-800 mt-2">{stats.inProgress}</p>
          </div>
          
          <div className="bg-emerald-50 rounded-lg border border-emerald-200 p-4 sm:p-5">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-emerald-700">Complete</span>
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-emerald-800 mt-2">{stats.complete}</p>
          </div>
          
          <div className="bg-rose-50 rounded-lg border border-rose-200 p-4 sm:p-5">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-rose-700">Overdue</span>
              <AlertCircle className="w-5 h-5 text-rose-600" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-rose-800 mt-2">{stats.overdue}</p>
          </div>
        </div>

        {/* Filters and Add Button */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-500">Filter:</span>
            </div>
            <Select value={functionFilter} onValueChange={setFunctionFilter}>
              <SelectTrigger className="w-[160px]" data-testid="function-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {functions.map(f => (
                  <SelectItem key={f.value} value={f.value}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px]" data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statuses.map(s => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {canEdit && (
            <Button 
              onClick={() => { resetForm(); setIsAddDialogOpen(true); }}
              className="bg-[#2563EB] hover:bg-[#1D4ED8]"
              data-testid="add-action-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Action
            </Button>
          )}
        </div>

        {/* Actions Table */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-[50px]">Status</TableHead>
                  <TableHead className="min-w-[250px]">Description</TableHead>
                  <TableHead className="w-[120px]">Function</TableHead>
                  <TableHead className="w-[150px]">Owner</TableHead>
                  <TableHead className="w-[120px]">Due Date</TableHead>
                  <TableHead className="w-[120px]">Created By</TableHead>
                  {canEdit && <TableHead className="w-[180px]">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {actions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canEdit ? 7 : 6} className="text-center py-8 text-slate-400">
                      No action items found
                    </TableCell>
                  </TableRow>
                ) : (
                  actions.map((action) => (
                    <TableRow key={action.id} className={cn(
                      action.status === 'complete' && 'bg-slate-50/50'
                    )}>
                      <TableCell>
                        {getStatusIcon(action.status)}
                      </TableCell>
                      <TableCell className={cn(
                        "font-medium",
                        action.status === 'complete' && 'text-slate-400 line-through'
                      )}>
                        {action.description}
                      </TableCell>
                      <TableCell className="capitalize">{action.function}</TableCell>
                      <TableCell>{action.owner}</TableCell>
                      <TableCell>
                        <span className={cn(
                          new Date(action.due_date) < new Date() && action.status !== 'complete'
                            ? 'text-rose-600 font-medium'
                            : 'text-slate-600'
                        )}>
                          {new Date(action.due_date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </span>
                      </TableCell>
                      <TableCell className="text-slate-500">{action.created_by_name}</TableCell>
                      {canEdit && (
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Select
                              value={action.status}
                              onValueChange={(val) => handleStatusChange(action.id, val)}
                            >
                              <SelectTrigger className="h-8 text-xs w-[100px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="open">Open</SelectItem>
                                <SelectItem value="in_progress">In Progress</SelectItem>
                                <SelectItem value="complete">Complete</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => openEditDialog(action)}
                              data-testid={`edit-action-${action.id}`}
                            >
                              <Pencil className="w-4 h-4 text-slate-500" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 text-rose-500 hover:text-rose-700"
                              onClick={() => openDeleteDialog(action)}
                              data-testid={`delete-action-${action.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      {/* Add Action Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Action Item</DialogTitle>
            <DialogDescription>
              Create a new action item to track across the organization.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                placeholder="Describe the action item..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                data-testid="action-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="owner">Owner *</Label>
                <Input
                  id="owner"
                  placeholder="e.g., VP Sales"
                  value={formData.owner}
                  onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                  data-testid="action-owner-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="function">Function *</Label>
                <Select 
                  value={formData.function} 
                  onValueChange={(val) => setFormData({ ...formData, function: val })}
                >
                  <SelectTrigger data-testid="action-function-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="brokerage">Brokerage</SelectItem>
                    <SelectItem value="mga">MGA</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                    <SelectItem value="finance">Finance</SelectItem>
                    <SelectItem value="people">People</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="due_date">Due Date *</Label>
                <Input
                  id="due_date"
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  data-testid="action-due-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select 
                  value={formData.status} 
                  onValueChange={(val) => setFormData({ ...formData, status: val })}
                >
                  <SelectTrigger data-testid="action-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="complete">Complete</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddAction} className="bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="save-action-btn">
              Create Action
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Action Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Action Item</DialogTitle>
            <DialogDescription>
              Update the action item details.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description *</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-owner">Owner *</Label>
                <Input
                  id="edit-owner"
                  value={formData.owner}
                  onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-function">Function *</Label>
                <Select 
                  value={formData.function} 
                  onValueChange={(val) => setFormData({ ...formData, function: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="brokerage">Brokerage</SelectItem>
                    <SelectItem value="mga">MGA</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                    <SelectItem value="finance">Finance</SelectItem>
                    <SelectItem value="people">People</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-due_date">Due Date *</Label>
                <Input
                  id="edit-due_date"
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-status">Status</Label>
                <Select 
                  value={formData.status} 
                  onValueChange={(val) => setFormData({ ...formData, status: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="complete">Complete</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditAction} className="bg-[#2563EB] hover:bg-[#1D4ED8]">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-rose-600">Delete Action Item</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this action item? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {selectedAction && (
            <div className="py-4">
              <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
                "{selectedAction.description}"
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteAction}
              data-testid="confirm-delete-btn"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default ActionLog;
